
import React, { useEffect, useState } from "react";
import {
    Box,
    Grid,
    InputAdornment,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch } from "react-redux";
import { api } from "../../../utils/api";
import { endpoints } from "../../../utils/endpoints";
import { updateGlobalLoader } from "../../../components/backdrop/backdropSlice";
import { getFixedValue, TABLE_BODY_ROW_MUI_STYLES, TABLE_HEADER_ROW_MUI_STYLES } from "../../../utils/constant";
import moment from "moment";
import CustomPagination from "../../../utils/CustomPagination";

const AllBills = () => {
    const dispatch = useDispatch();
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState({
        type: "",
    });
    const [data, setData] = useState({ list: [], total: 0 });
    const [userType, setUserType] = useState("");

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("user"));
        if (user) {
            setUserType(user.userType);
        }
    }, []);


    // Correcting fetch implementation to match project pattern
    const loadData = () => {
        dispatch(updateGlobalLoader(true));

        // Prepare query params manually as api.getApiCall appends param string to endpoint
        let queryParams = `?page=${page}&limit=${limit}`;
        if (search) queryParams += `&search=${search}`;
        if (filters.type) {
            const filterObj = { type: filters.type };
            queryParams += `&filters=${JSON.stringify(filterObj)}`;
        }

        api.getApiCall(
            endpoints.allBills,
            queryParams,
            (respData) => {
                dispatch(updateGlobalLoader(false));
                if (respData && respData.data && respData.data.result) {
                    setData(respData.data.result);
                }
            },
            (error) => {
                dispatch(updateGlobalLoader(false));
                console.error(error);
                // Optionally handle error alert here
            }
        );
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, limit, search, filters]);

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setPage(1);
    };

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
        setPage(1);
    };

    const handleChange = (value) => {
        setPage(value);
    };

    const handleChangePerPage = (event) => {
        setLimit(event.target.value);
        setPage(1);
    };

    return (
        <>
            <Box sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h5" fontWeight="bold"> All Bills </Typography>
                </Stack>

                <Paper sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <OutlinedInput
                                fullWidth
                                placeholder="Search by Milestone, Campaign, Brand, etc."
                                startAdornment={
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                }
                                value={search}
                                onChange={handleSearch}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Select
                                fullWidth
                                displayEmpty
                                name="type"
                                value={filters.type}
                                onChange={handleFilterChange}
                                size="small"
                            >
                                <MenuItem value="">All Types</MenuItem>
                                <MenuItem value="paid">Paid</MenuItem>
                                <MenuItem value="barter">Barter</MenuItem>
                                <MenuItem value="affiliate">Affiliate</MenuItem>
                                <MenuItem value="performance">Performance</MenuItem>
                            </Select>
                        </Grid>
                    </Grid>
                </Paper>

                <Box sx={{
                    backgroundColor: '#fff',
                    transition: 'all .5s ease-in-out',
                    position: 'relative',
                    border: '1px solid rgba(170, 170, 170, 0.3)',
                }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={TABLE_HEADER_ROW_MUI_STYLES}>
                                    <TableCell><b>Invoice</b></TableCell>
                                    <TableCell><b>Milestone</b></TableCell>
                                    <TableCell><b>Campaign</b></TableCell>
                                    <TableCell><b>Type</b></TableCell>
                                    {userType !== 'company' && <TableCell><b>Company</b></TableCell>}
                                    <TableCell><b>Brand</b></TableCell>
                                    <TableCell><b>Influencer</b></TableCell>
                                    <TableCell><b>Amount</b></TableCell>
                                    <TableCell><b>Status</b></TableCell>
                                    <TableCell><b>Invoice Date</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.list && data.list.length > 0 ? (
                                    data.list.map((row) => (
                                        <TableRow key={row.id} sx={TABLE_BODY_ROW_MUI_STYLES}>
                                            <TableCell>
                                                {row.invoiceUrl ? (
                                                    <a href={row.invoiceUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 'bold' }}>
                                                        {row.invoiceNo || 'View Invoice'}
                                                    </a>
                                                ) : (
                                                    row.invoiceNo || '-'
                                                )}
                                            </TableCell>
                                            <TableCell>{row.title}</TableCell>
                                            <TableCell>{row.campaignName}</TableCell>
                                            <TableCell sx={{ textTransform: 'capitalize' }}>{row.campaignType}</TableCell>
                                            {userType !== 'company' && <TableCell>{row.companyName || '-'}</TableCell>}
                                            <TableCell>{row.brandName || '-'}</TableCell>
                                            <TableCell>
                                                <Stack direction="row" alignItems="center" gap={1}>
                                                    {/* Add Avatar if needed using row.influencerPicture */}
                                                    {row.influencerName}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>{getFixedValue(row.amount)}</TableCell>
                                            <TableCell sx={{ textTransform: 'capitalize' }}>
                                                <span style={{
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    backgroundColor: row.paymentStatus === 'completed' ? '#e8f5e9' :
                                                        row.paymentStatus === 'pending' ? '#fff3e0' : '#ffebee',
                                                    color: row.paymentStatus === 'completed' ? '#2e7d32' :
                                                        row.paymentStatus === 'pending' ? '#ef6c00' : '#c62828'
                                                }}>
                                                    {row.paymentStatus}
                                                </span>
                                            </TableCell>
                                            <TableCell>{row.invoiceDate ? moment(row.invoiceDate).format('DD MMM YYYY') : '-'}</TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center">
                                            No bills found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Stack sx={{ background: 'transparent', height: '60px', border: '1px solid #E9EAEB' }} direction="row" alignItems={"center"} justifyContent={"flex-end"}>
                        <CustomPagination
                            count={Math.ceil(data.total / limit)}
                            onChange={handleChange}
                            currentPage={page - 1} // 0-based for CustomPagination
                            handleChangePerPage={handleChangePerPage}
                            limit={limit}
                            totalCount={data.total}
                            listingCount={data.list?.length || 0}
                            defaultPage={1} siblingCount={1} boundaryCount={1}
                        />
                    </Stack>
                </Box>
            </Box>
        </>
    );
};

export default AllBills;
